import Axios from "axios";
import { put, takeEvery, takeLatest } from "redux-saga/effects";
import {
    restrictionReceived,
    subRestrictionReceived,
    timeLimitValuesReceived,
    subActivityDataReceived,
    permitParkingValuesReceived,
    storePermitsData,
    storePriceData
} from "../actions/Filters";
import {
    GET_RESTRICTIONS,
    GET_SUB_RESTRICTIONS,
    GET_TIME_LIMIT_VALUES,
    PUT_OCCUPANCY_DATA,
    GetRestrictions,
    GetSubRestrictions,
    PUT_SUB_ACTIVITY_DATA,
    PutSubActivityData,
    PutOccupancyData,
    RestrictionReceived,
    SubActivityDataReceived,
    SubRestrictionReceived,
    TimeLimitValuesReceived,
    GET_PERMIT_PARKING_VALUES,
    GetPermitParkingValues,
    PermitParkingValuesReceived,
    GET_PERMIT_DATA_VALUES,
    StorePermitsData,
    GetPermitDataValues,
    GetPriceDataValues,
    StorePricedata
} from "../actions/Filters/types";
import { getRequest, showError } from "../components/commonUtilities";
import { decrypt } from "../Utilities/decryption";
import { GeneratorYieldType } from "../interfaces/ActionReducer/GetMethod/GetResponse";
import { ANALZER_DASHBOARD_VIEW_ROUTE } from "../constants";

function* getRestrictions(
    action: GetRestrictions
): Generator<
    GeneratorYieldType<RestrictionReceived["payload"]> | Promise<RestrictionReceived>,
    void,
    RestrictionReceived["payload"]["data"]
> {
    try {
        const json = yield getRequest("/api/activity/", action.payload.params);

        yield put(restrictionReceived({ data: json }));
    } catch (e) {
        showError(e);
    }
}

export function* getRestrictionsSaga() {
    yield takeLatest(GET_RESTRICTIONS, getRestrictions);
}

function* putSubActivityData(action: PutSubActivityData) {
    try {
        yield Axios.patch(
            `/api/subactivity/${action.payload.data.selectedRestiction}/
            `,
            action.payload.data.selectedOccupancyPercent
                ? {
                      occupancy_percent: action.payload.data.selectedOccupancyPercent
                  }
                : {
                      curbside_users: action.payload.data.selectedUserNumber || 0,
                      curbside_function_id: action.payload.data.selectedCurbsideFunction
                  }
        ).then((response) => {
            return response.data;
        });

        if (action.payload.afterProcess) {
            action.payload.afterProcess();
        }
    } catch (error) {
        showError(error);
    }
}

export function* setRestictionsSaga() {
    yield takeEvery(PUT_SUB_ACTIVITY_DATA, putSubActivityData);
}

function* putOccupancyData(action: PutOccupancyData) {
    try {
        yield Axios.patch("/api/subactivity/update_occupancy_percent/", {
            occupancy_percent: action.payload.data
        }).then((response) => {
            return response.data;
        });
        if (action.payload.afterProcess) {
            action.payload.afterProcess();
        }
    } catch (error) {
        showError(error);
    }
}

export function* setOccupancyDataSaga() {
    yield takeEvery(PUT_OCCUPANCY_DATA, putOccupancyData);
}

function* getSubRestrictions(
    action: GetSubRestrictions
): Generator<
    | Promise<SubRestrictionReceived>
    | Promise<SubActivityDataReceived>
    | GeneratorYieldType<SubActivityDataReceived["payload"]>
    | GeneratorYieldType<SubActivityDataReceived["payload"]>,
    void,
    SubActivityDataReceived["payload"]["data"] | SubRestrictionReceived["payload"]["data"]
> {
    try {
        const json = yield getRequest("/api/subactivity/", action.payload.params);

        // this data is required for demand aggregated occupancy
        if (window.location.pathname.includes(ANALZER_DASHBOARD_VIEW_ROUTE)) {
            json.results.push({
                id: -1,
                dash_style: "solid",
                curbside_function: {
                    name: "Storage (Parking)",
                    id: 3,
                    modified: "null",
                    created: "null"
                },
                display_name: "Multiple Regulations",
                name: "Multiple Regulations",
                dashboard_color: "#90CAF9",
                occupancy_percent: 100,
                curbside_users: 1,
                editable_parking_price: true
            } as any);
        }
        yield put(
            action.payload.params.name
                ? subActivityDataReceived({ data: json })
                : subRestrictionReceived({ data: json })
        );
    } catch (e) {
        showError(e);
    }
}

export function* getSubRestrictionsSaga() {
    yield takeLatest(GET_SUB_RESTRICTIONS, getSubRestrictions);
}

function* getTimeLimitValues(): Generator<
    Promise<TimeLimitValuesReceived> | GeneratorYieldType<TimeLimitValuesReceived["payload"]>,
    void,
    TimeLimitValuesReceived["payload"]["data"]
> {
    try {
        let json = yield getRequest("api/regulation/get_distinct_time_limits/");
        json = decrypt(json);
        yield put(timeLimitValuesReceived({ data: json }));
    } catch (e) {
        showError(e);
    }
}

export function* getTimeLimitValuesSaga() {
    yield takeLatest(GET_TIME_LIMIT_VALUES, getTimeLimitValues);
}

function* getPermitParkingValues(
    action: GetPermitParkingValues
): Generator<
    | Promise<PermitParkingValuesReceived>
    | GeneratorYieldType<PermitParkingValuesReceived["payload"]>,
    void,
    PermitParkingValuesReceived["payload"]["data"]
> {
    try {
        const json = yield getRequest("/api/permits/", action.payload.params);
        yield put(permitParkingValuesReceived({ data: json }));
    } catch (error) {
        showError(error);
    }
}
export function* getPermitParkingValuesSaga() {
    yield takeLatest(GET_PERMIT_PARKING_VALUES, getPermitParkingValues);
}

function* getPermitDataValues(
    action: GetPermitDataValues
): Generator<
    Promise<StorePermitsData> | GeneratorYieldType<StorePermitsData["payload"]>,
    void,
    StorePermitsData["payload"]["data"]
> {
    try {
        const json = yield getRequest(
            `api/links/get_sub_activities_and_permits/?current_time=${action.payload.time}&date=${action.payload.date}`
        );

        yield put(storePermitsData({ data: json }));
    } catch (error) {
        showError(error);
    }
}
export function* getPermitDataValuesSaga() {
    yield takeLatest(GET_PERMIT_DATA_VALUES, getPermitDataValues);
}

function* getPriceDataValues(
    action: GetPriceDataValues
): Generator<
    Promise<StorePricedata> | GeneratorYieldType<StorePricedata["payload"]>,
    void,
    StorePricedata["payload"]["data"]
> {
    try {
        const json = yield getRequest(
            `api/links/get_sub_activities_and_parking_rates/?current_time=${action.payload.time}&date=${action.payload.date}`
        );
        yield put(
            storePriceData({
                data: json
            })
        );
    } catch (error) {
        showError(error);
    }
}

export function* getPriceDataValuesSaga() {
    yield takeLatest("GET_PRICE_DATA_VALUES", getPriceDataValues);
}
